import React, { useState, useEffect } from "react";
import { initializeIcons } from "@fluentui/react";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback, Security, useOktaAuth } from "@okta/okta-react";
import { Route, Routes, useNavigate } from "react-router-dom";
import Loading from "./components/Loading";
import { RequiredAuth } from "./components/SecureRoute";
import config from "./config";
import { Ask } from "./pages/ask/Ask";
import Chat from "./pages/chat/Chat";
import RiskAnalysis from "./pages/riskAnalysis/RiskAnalysis";
import Layout from "./pages/layout/Layout";
import { NoPage } from "./pages/NoPage";
import { PowerPlantProvider } from "./components/powerplant";
import ElisaBot from "./pages/elisabot/ElisaBot";
import GDPRModal from "./components/GDPRModal/GDPRModal";
import ChatAgent from "./pages/chat_agent/ChatAgent";
import RcaAgent from "./pages/rca_agent/RcaAgent";
import SwaggerPage from "./pages/swaggerui/SwaggerPage";
import OrchestratorV2Test from "./pages/orchestrator_v2_test/OrchestratorV2Test";
import ChatHistory from "./pages/chat_history/ChatHistory";

const oktaAuth = new OktaAuth(config.oidc);
initializeIcons();

const AppContent = () => {
    const { authState } = useOktaAuth();
    const [showGDPRModal, setShowGDPRModal] = useState(false);
    const [gdprAccepted, setGdprAccepted] = useState(false);

    useEffect(() => {
        // Only check GDPR after authentication is resolved
        if (authState && authState.isAuthenticated) {
            const lastAccepted = localStorage.getItem("gdpr-accepted");
            if (!lastAccepted) {
                setShowGDPRModal(true);
                setGdprAccepted(false);
                return;
            }

            const lastAcceptedDate = new Date(lastAccepted);
            const now = new Date();
            const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());

            const shouldShow = lastAcceptedDate < oneMonthAgo;
            setShowGDPRModal(shouldShow);
            setGdprAccepted(!shouldShow);
        }
    }, [authState]);

    const handleCloseGDPRModal = () => {
        localStorage.setItem("gdpr-accepted", new Date().toISOString());
        setShowGDPRModal(false);
        setGdprAccepted(true);
    };

    return (
        <PowerPlantProvider>
            <Routes>
                <Route path="/login/callback" element={<LoginCallback loadingElement={<Loading />} />} />
                <Route path="/" element={<RequiredAuth />}>
                    <Route path="swagger-ui" element={<SwaggerPage />} />
                    <Route path="" element={<Layout onShowGDPRModal={() => setShowGDPRModal(true)} />}>
                        <Route path="" element={<Chat gdprAccepted={gdprAccepted} />} index={true} />
                        <Route path="orch-v2-test" element={<OrchestratorV2Test />} />
                        <Route path="chat-history" element={<ChatHistory gdprAccepted={gdprAccepted} />} />
                        <Route path="risk_analysis" element={<RiskAnalysis />} index={true} />
                        <Route path="ask_elisabot" element={<ElisaBot />} index={true} />
                        <Route path="chat_agent" element={<ChatAgent />} index={true} />
                        <Route path="rca_agent" element={<RcaAgent />} index={true} />
                        <Route path="*" element={<NoPage />} />
                    </Route>
                </Route>
            </Routes>

            <GDPRModal isOpen={showGDPRModal} onClose={handleCloseGDPRModal} />
        </PowerPlantProvider>
    );
};

const App = () => {
    const navigate = useNavigate();

    const restoreOriginalUri = (_oktaAuth: any, originalUri: string) => {
        navigate(toRelativeUrl(originalUri || "/", window.location.origin));
    };

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <AppContent />
        </Security>
    );
};

export default App;
